import React from 'react'

/*
 * Classic / Modern admin pages — the same switch the dashboard has in its
 * header ("Classic view"). Modern means the _v2 pages; each has a classic
 * twin, listed in PAIRS. The choice is kept in localStorage, shared by every
 * page, and PrivatePage keeps the address on the chosen side (see
 * useViewRedirect), so menu links and old bookmarks land on the right page.
 */
const VIEW_KEY = 'adminView'
const CHANGE_EVENT = 'adminViewChange'

export type AdminView = 'classic' | 'modern'

/* [classic, modern] — `:id` is carried across */
const PAIRS: [RegExp, (m: RegExpMatchArray) => string, RegExp, (m: RegExpMatchArray) => string][] = [
  [/^\/$/, () => '/dashboard_v2', /^\/dashboard_v2\/?$/, () => '/'],
  [/^\/users\/subscribers\/?$/, () => '/users/subscribers_v2', /^\/users\/subscribers_v2\/?$/, () => '/users/subscribers'],
  [/^\/users\/subscriber\/([^/]+)\/?$/, (m) => `/users/subscriber_v2/${m[1]}`, /^\/users\/subscriber_v2\/([^/]+)\/?$/, (m) => `/users/subscriber/${m[1]}`],
  [/^\/package\/?$/, () => '/package_v2', /^\/package_v2\/?$/, () => '/package'],
  [/^\/marketing\/?$/, () => '/marketing_v2', /^\/marketing_v2\/?$/, () => '/marketing'],
  [/^\/payment\/?$/, () => '/payment_v2', /^\/payment_v2\/?$/, () => '/payment'],
  [/^\/system\/?$/, () => '/system_v2', /^\/system_v2\/?$/, () => '/system'],
  [/^\/support\/?$/, () => '/support_v2', /^\/support_v2\/?$/, () => '/support'],
  [/^\/settings\/?$/, () => '/settings_v2', /^\/settings_v2\/?$/, () => '/settings'],
  [/^\/apikey\/?$/, () => '/apikey_v2', /^\/apikey_v2\/?$/, () => '/apikey'],
  [/^\/users\/enterprise\/?$/, () => '/users/enterprise_v2', /^\/users\/enterprise_v2\/?$/, () => '/users/enterprise'],
  [/^\/users\/admins\/?$/, () => '/users/admins_v2', /^\/users\/admins_v2\/?$/, () => '/users/admins'],
  [/^\/qr-registration\/?$/, () => '/qr-registration_v2', /^\/qr-registration_v2\/?$/, () => '/qr-registration'],
  [/^\/result\/([^/]+)\/?$/, (m) => `/result_v2/${m[1]}`, /^\/result_v2\/([^/]+)\/?$/, (m) => `/result/${m[1]}`],
]

/** The same page in the other view, or null when this page has no twin. */
export function pathInView(pathname: string, view: AdminView): string | null {
  for (const [classicRe, toModern, modernRe, toClassic] of PAIRS) {
    if (view === 'modern') {
      const m = pathname.match(classicRe)
      if (m) return toModern(m)
    } else {
      const m = pathname.match(modernRe)
      if (m) return toClassic(m)
    }
  }
  return null
}

function readView(): AdminView {
  try {
    const saved = localStorage.getItem(VIEW_KEY)
    if (saved === 'classic' || saved === 'modern') return saved
  } catch (e) {}
  // classic until someone switches — the v2 pages do not cover every section yet
  return 'classic'
}

export function useAdminView() {
  const [view, setViewState] = React.useState<AdminView>(readView)

  // every mounted toggle and PrivatePage follow a change made by any of them
  React.useEffect(() => {
    const sync = () => setViewState(readView())
    window.addEventListener(CHANGE_EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const setView = React.useCallback((next: AdminView) => {
    try {
      localStorage.setItem(VIEW_KEY, next)
    } catch (e) {}
    setViewState(next)
    window.dispatchEvent(new Event(CHANGE_EVENT))
  }, [])

  return { view, setView }
}
